/**
 * Anoura — delivery date & time picker
 *
 * One engine, two placements (data-mode):
 *
 *   mode="product"  Writes the choice as LINE ITEM PROPERTIES into the product
 *                   form. Critical detail: Horizon renders the "Buy it now"
 *                   payment button inside {% form 'product' %}, so form fields
 *                   travel with express checkout — whereas cart attributes do
 *                   NOT, because dynamic checkout bypasses the cart. Storing
 *                   the choice here is the only way a Buy-it-now order keeps
 *                   its delivery date.
 *
 *   mode="cart"     Writes cart attributes via /cart/update.js (order-level).
 *
 * Date maths is shared with the product-page estimate (window.AnouraDelivery),
 * so nothing can offer a day you don't deliver, a date inside a product's lead
 * time, or a same-day slot whose ordering window has closed.
 */
(function () {
  'use strict';

  var ROOT = '[data-anoura-scheduler]';
  var PRODUCT_FORM = 'form[data-type="add-to-cart-form"]';

  function core() {
    return window.AnouraDelivery;
  }

  /** Parse "14:00 - 16:00 | 12" into { label, latestHour }. */
  function parseSlots(raw, fallbackHour) {
    return String(raw || '')
      .split('\n')
      .map(function (line) { return line.trim(); })
      .filter(Boolean)
      .map(function (line) {
        var parts = line.split('|');
        var latest = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
        return { label: parts[0].trim(), latestHour: isNaN(latest) ? fallbackHour : latest };
      });
  }

  function isoOf(date) {
    return date.toISOString().slice(0, 10);
  }

  function utc(year, month, day) {
    return new Date(Date.UTC(year, month, day));
  }

  function saveAttributes(attributes) {
    var url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    });
  }

  /** Create or update a hidden field inside every add-to-cart form on the page. */
  function writeFormField(name, value) {
    document.querySelectorAll(PRODUCT_FORM).forEach(function (form) {
      var input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value || '';
    });
  }

  function build(el) {
    var api = core();
    if (!api) return; // estimate script missing — leave the page untouched

    var mode = el.dataset.mode === 'product' ? 'product' : 'cart';
    var days = (el.dataset.days || '')
      .split(',')
      .filter(function (v) { return v !== ''; })
      .map(Number);
    if (!days.length) {
      el.hidden = true;
      return;
    }

    var timeZone = el.dataset.timezone || 'Asia/Dubai';
    var cutoff = parseInt(el.dataset.cutoff, 10) || 0;
    var lead = parseInt(el.dataset.lead, 10) || 0;
    var count = parseInt(el.dataset.count, 10) || 14;
    var locale = el.dataset.locale || 'en';
    var todayLabel = el.dataset.labelToday || 'Today';
    var tomorrowLabel = el.dataset.labelTomorrow || 'Tomorrow';
    var attrDate = el.dataset.attrDate || 'Delivery date';
    var attrSlot = el.dataset.attrSlot || 'Delivery time';
    var slots = parseSlots(el.dataset.slots, Math.floor(cutoff / 60));

    var now;
    try {
      now = api.zoneNow(timeZone);
    } catch (e) {
      el.hidden = true;
      return;
    }

    var today = api.startOfDay(now);
    var earliest = api.deliveryDate(now, cutoff, days, lead, false);
    var builtPastCutoff = now.getUTCHours() * 60 + now.getUTCMinutes() >= cutoff;

    // Last offered date = `count` deliverable days from the earliest one.
    var latest = new Date(earliest);
    var found = 1;
    var guard = 0;
    while (found < count && guard++ < 400) {
      latest.setUTCDate(latest.getUTCDate() + 1);
      if (days.indexOf(latest.getUTCDay()) !== -1) found++;
    }

    var calendarEl = el.querySelector('[data-scheduler-calendar]');
    var slotList = el.querySelector('[data-scheduler-slots]');
    var chosenEl = el.querySelector('[data-scheduler-chosen]');
    if (!calendarEl || !slotList) return;

    function selectable(date) {
      return (
        date.getTime() >= earliest.getTime() &&
        date.getTime() <= latest.getTime() &&
        days.indexOf(date.getUTCDay()) !== -1
      );
    }

    function readableOf(date) {
      return api.dateLabel(date, today, locale, todayLabel, tomorrowLabel);
    }

    // Restore a previous choice, else fall back to the earliest date.
    var state = { selected: new Date(earliest), slot: el.dataset.savedSlot || '' };
    var savedDate = el.dataset.savedDate || '';
    if (savedDate) {
      var probe = new Date(earliest);
      for (var i = 0; i < 400; i++) {
        if (probe.getTime() > latest.getTime()) break;
        if (selectable(probe) && readableOf(probe) === savedDate) {
          state.selected = new Date(probe);
          break;
        }
        probe.setUTCDate(probe.getUTCDate() + 1);
      }
    }
    var view = { year: state.selected.getUTCFullYear(), month: state.selected.getUTCMonth() };

    function renderCalendar() {
      calendarEl.innerHTML = '';

      var nav = document.createElement('div');
      nav.className = 'anoura-cal__nav';

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'anoura-cal__arrow';
      prev.setAttribute('aria-label', 'Previous month');
      prev.textContent = '‹';

      var title = document.createElement('span');
      title.className = 'anoura-cal__title';
      title.textContent = new Intl.DateTimeFormat(locale, {
        month: 'long', year: 'numeric', timeZone: 'UTC',
      }).format(utc(view.year, view.month, 1));

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'anoura-cal__arrow';
      next.setAttribute('aria-label', 'Next month');
      next.textContent = '›';

      var firstOfView = utc(view.year, view.month, 1);
      var lastOfView = utc(view.year, view.month + 1, 0);
      prev.disabled = firstOfView.getTime() <= utc(earliest.getUTCFullYear(), earliest.getUTCMonth(), 1).getTime();
      next.disabled = lastOfView.getTime() >= utc(latest.getUTCFullYear(), latest.getUTCMonth() + 1, 0).getTime();

      prev.addEventListener('click', function () {
        view.month -= 1;
        if (view.month < 0) { view.month = 11; view.year -= 1; }
        renderCalendar();
      });
      next.addEventListener('click', function () {
        view.month += 1;
        if (view.month > 11) { view.month = 0; view.year += 1; }
        renderCalendar();
      });

      nav.append(prev, title, next);
      calendarEl.appendChild(nav);

      var grid = document.createElement('div');
      grid.className = 'anoura-cal__grid';

      var narrow = new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' });
      for (var d = 0; d < 7; d++) {
        var dow = document.createElement('span');
        dow.className = 'anoura-cal__dow';
        dow.textContent = narrow.format(utc(2024, 0, 7 + d)); // 7 Jan 2024 was a Sunday
        grid.appendChild(dow);
      }

      var blanks = utc(view.year, view.month, 1).getUTCDay();
      for (var b = 0; b < blanks; b++) {
        var filler = document.createElement('span');
        filler.className = 'anoura-cal__day anoura-cal__day--empty';
        grid.appendChild(filler);
      }

      var daysInMonth = utc(view.year, view.month + 1, 0).getUTCDate();
      for (var day = 1; day <= daysInMonth; day++) {
        var date = utc(view.year, view.month, day);
        var ok = selectable(date);
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'anoura-cal__day' + (ok ? ' anoura-cal__day--available' : '');
        button.textContent = String(day);
        button.dataset.iso = isoOf(date);
        button.disabled = !ok;
        if (ok) {
          button.title = readableOf(date);
          if (isoOf(date) === isoOf(state.selected)) button.classList.add('is-selected');
          button.addEventListener('click', function (event) {
            state.selected = new Date(event.currentTarget.dataset.iso + 'T00:00:00.000Z');
            state.slot = ''; // a new date can invalidate the previous slot
            renderCalendar();
            renderSlots();
            persist();
          });
        }
        grid.appendChild(button);
      }

      calendarEl.appendChild(grid);
      updateTrigger();
    }

    /**
     * Trigger row shows the whole choice at a glance: date, then slot. Until a
     * slot is picked it prompts for one — otherwise the time-window option is
     * invisible to anyone who never opens the modal.
     */
    function updateTrigger() {
      if (!chosenEl) return;
      var parts = [readableOf(state.selected)];
      parts.push(state.slot || el.dataset.slotPrompt || 'Choose a time window');
      chosenEl.textContent = parts.join(' · ');
      chosenEl.classList.toggle('is-awaiting-slot', !state.slot);
    }

    /**
     * Live "Order within 2h 14m" under the chosen date. Shown only while
     * beating today's cutoff still buys an earlier delivery date — otherwise
     * it would be manufacturing urgency that changes nothing.
     */
    function updateCountdown() {
      var out = el.querySelector('[data-scheduler-countdown]');
      if (!out || !api.countdownLabel) return;

      var fresh;
      try {
        fresh = api.zoneNow(timeZone);
      } catch (e) {
        out.hidden = true;
        return;
      }

      var minutesNow = fresh.getUTCHours() * 60 + fresh.getUTCMinutes();
      var past = minutesNow >= cutoff;

      // The cutoff just passed while the page sat open — the whole calendar's
      // earliest date has shifted, so rebuild rather than show stale options.
      if (past && !builtPastCutoff) {
        el.dataset.built = '';
        el.__anouraTick = null;
        scheduleInit();
        return;
      }

      var ifOrderedNow = api.deliveryDate(fresh, cutoff, days, lead, false);
      var ifMissed = api.deliveryDate(fresh, cutoff, days, lead, true);
      if (past || ifOrderedNow.getTime() === ifMissed.getTime()) {
        out.hidden = true;
        return;
      }

      var cutoffAt = api.startOfDay(fresh).getTime() + cutoff * 60000;
      var template = out.dataset.template || 'Order within [countdown]';
      out.textContent = template.replace('[countdown]', api.countdownLabel(cutoffAt - fresh.getTime()));
      out.hidden = false;
    }

    function renderSlots() {
      var isToday = isoOf(state.selected) === isoOf(today);
      var hourNow = now.getUTCHours();

      slotList.innerHTML = '';
      var anyAvailable = false;

      slots.forEach(function (slot) {
        var closed = isToday && hourNow >= slot.latestHour;
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'anoura-scheduler__slot';
        button.textContent = slot.label;
        button.dataset.slot = slot.label;
        if (closed) {
          button.disabled = true;
          button.title = 'Ordering for this slot has closed today';
        } else {
          anyAvailable = true;
          if (slot.label === state.slot) button.classList.add('is-selected');
          button.addEventListener('click', function (event) {
            state.slot = event.currentTarget.dataset.slot;
            renderSlots();
            persist();
          });
        }
        slotList.appendChild(button);
      });

      if (!anyAvailable && slots.length) {
        var note = document.createElement('p');
        note.className = 'anoura-scheduler__note';
        note.textContent = 'No slots left for this date — please choose another day.';
        slotList.appendChild(note);
      }
      updateTrigger();
      syncRequirement();
    }

    function apply() {
      var readable = readableOf(state.selected);
      if (mode === 'product') {
        writeFormField('properties[' + attrDate + ']', readable);
        writeFormField('properties[' + attrSlot + ']', state.slot);
      }
      // Expose for the custom-bouquet builder, which adds to cart via its own
      // fetch and merges this into its line item properties.
      window.AnouraDeliverySelection = { date: readable, slot: state.slot, attrDate: attrDate, attrSlot: attrSlot };
    }

    function persist() {
      var readable = readableOf(state.selected);
      el.dataset.savedDate = readable;
      el.dataset.savedSlot = state.slot;
      apply();
      if (mode === 'cart') {
        var attributes = {};
        attributes[attrDate] = readable;
        attributes[attrSlot] = state.slot;
        saveAttributes(attributes).catch(function () {});
      }
      syncRequirement();
    }

    /** Optionally hold the buy button until a slot is chosen. */
    function syncRequirement() {
      if (el.dataset.require !== 'true') return;
      var ready = Boolean(!slots.length || state.slot);
      var selector = mode === 'product'
        ? 'add-to-cart-component button, .product-form-buttons button[type="submit"]'
        : '#checkout, .cart__checkout-button';
      document.querySelectorAll(selector).forEach(function (button) {
        button.disabled = !ready;
        button.classList.toggle('is-awaiting-delivery', !ready);
      });
      var hint = el.querySelector('[data-scheduler-hint]');
      if (hint) hint.hidden = ready;
    }

    // Modal wiring. <dialog> gives focus trapping, Esc-to-close and a backdrop
    // for free; if the browser lacks showModal the picker stays inline instead
    // of becoming unreachable.
    var dialog = el.querySelector('[data-scheduler-dialog]');
    var openBtn = el.querySelector('[data-scheduler-open]');
    if (dialog && openBtn) {
      if (typeof dialog.showModal !== 'function') {
        dialog.setAttribute('open', '');
      } else {
        openBtn.addEventListener('click', function () {
          dialog.showModal();
        });
        el.querySelectorAll('[data-scheduler-close], [data-scheduler-confirm]').forEach(function (button) {
          button.addEventListener('click', function () {
            dialog.close();
          });
        });
        // Click on the backdrop (outside the panel) closes it.
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) dialog.close();
        });
      }
    }

    // Horizon re-renders the product form on variant change, which wipes the
    // hidden fields — re-apply them whenever the DOM settles.
    el.__anouraReapply = apply;

    el.__anouraTick = updateCountdown;

    renderCalendar();
    renderSlots();
    updateCountdown();
    apply();
    el.hidden = false;
  }

  function init() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    document.querySelectorAll(ROOT).forEach(function (el) {
      if (el.dataset.built === '1') {
        if (el.__anouraReapply) el.__anouraReapply();
        return;
      }
      el.dataset.built = '1';
      build(el);
    });
  }

  // build() writes to the DOM, which re-triggers the observer below; coalesce
  // so a burst of mutations costs one pass instead of one per node.
  var queued = false;
  function scheduleInit() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      init();
    }, 60);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', scheduleInit);
  document.addEventListener('shopify:cart:lines-update', scheduleInit);

  // One shared ticker drives every picker's countdown.
  setInterval(function () {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    document.querySelectorAll(ROOT).forEach(function (el) {
      if (el.__anouraTick) el.__anouraTick();
    });
  }, 1000);

  if (document.body) {
    new MutationObserver(scheduleInit).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
